import { mainTheme } from '../themes/mainTheme';

export const buttonVariants = [
	'strong',
	'outline',
	'dimmed',
	'ghost',
	'link',
	'danger',
] as const;

export type ButtonVariant = typeof buttonVariants[number] | LegacyButtonVariant;

// Pre-redesign names, kept so existing call sites keep compiling.
// ponytail: delete once no consumer passes them.
type LegacyButtonVariant = 'default' | 'white' | 'primary' | 'secondary' | 'gradient';

// `active` is the toggled/selected button; `pressed` is the pointer holding it down.
// Design gives them different colours, so they cannot share one entry.
type StateColorsDefinition = {
	default: string;
	hover: string;
	focus?: string;
	active: string;
	pressed: string;
	disabled: string;
}

export type IconPosition = 'left' | 'right';

export type ButtonColorDefinition = {
	background: StateColorsDefinition;
	color: StateColorsDefinition;
	border: StateColorsDefinition | null;
	/** Focus ring. Omit to get the default green ring from the theme. */
	focusRing?: string;
}

const sameColorEveryState = (color: string): StateColorsDefinition => ({
	default: color,
	hover: color,
	focus: color,
	active: color,
	pressed: color,
	disabled: color,
});

// Every variant shares one disabled label and fill, so disabled reads the same
// across the set instead of each variant fading itself.
const disabledText = mainTheme.button.disabled.color;
const disabledFill = mainTheme.button.disabled.background;

// The label brightens as the button is engaged and falls back once released —
// only strong and danger keep a colour of their own throughout.
const dimmedLabel: StateColorsDefinition = {
	default: mainTheme.text.secondary,
	hover: mainTheme.text.primary,
	focus: mainTheme.text.primary,
	active: mainTheme.text.primary,
	pressed: mainTheme.text.primary,
	disabled: disabledText,
};

const strongVariant: ButtonColorDefinition = {
	background: {
		default: mainTheme.fill.primary,
		hover: mainTheme.fill.brand,
		focus: mainTheme.fill.primary,
		active: mainTheme.fill.brand,
		pressed: mainTheme.fill.brandTertiary,
		disabled: disabledFill,
	},
	color: {
		default: mainTheme.text.invertedSecondary,
		hover: mainTheme.text.inverted,
		focus: mainTheme.text.invertedSecondary,
		active: mainTheme.text.inverted,
		pressed: mainTheme.text.inverted,
		disabled: disabledText,
	},
	border: null,
};

const outlineVariant: ButtonColorDefinition = {
	background: sameColorEveryState('transparent'),
	color: dimmedLabel,
	border: {
		default: mainTheme.border.interactive,
		hover: mainTheme.border.active,
		focus: mainTheme.border.active,
		active: mainTheme.border.active,
		// Pressed keeps the resting colour — the stroke thickens instead.
		pressed: mainTheme.border.interactive,
		disabled: mainTheme.border.disabled,
	},
};

const dimmedVariant: ButtonColorDefinition = {
	background: {
		default: mainTheme.fill.dimmed,
		hover: mainTheme.fill.dimmedSecondary,
		focus: mainTheme.fill.dimmed,
		active: mainTheme.fill.dimmed,
		pressed: mainTheme.fill.dimmed,
		disabled: disabledFill,
	},
	color: dimmedLabel,
	border: null,
};

// Ghost is dimmed without the resting fill: it only takes a wash under the pointer.
const ghostVariant: ButtonColorDefinition = {
	background: {
		default: 'transparent',
		hover: mainTheme.fill.dimmed,
		focus: 'transparent',
		active: 'transparent',
		pressed: mainTheme.fill.dimmed,
		disabled: 'transparent',
	},
	color: {
		...dimmedLabel,
		// Releasing drops the label back to its resting weight, the wash does the work.
		pressed: mainTheme.text.secondary,
	},
	border: null,
};

// Link is ghost with no fill at all. The underline is permanent — buttonStyling
// draws it off the `textButton` flag.
const linkVariant: ButtonColorDefinition = {
	...ghostVariant,
	background: sameColorEveryState('transparent'),
};

const dangerVariant: ButtonColorDefinition = {
	background: {
		default: mainTheme.fill.danger,
		hover: mainTheme.fill.dangerSecondary,
		focus: mainTheme.fill.danger,
		active: mainTheme.fill.danger,
		pressed: mainTheme.fill.dangerSecondary,
		disabled: disabledFill,
	},
	color: {
		...sameColorEveryState(mainTheme.text.danger),
		disabled: disabledText,
	},
	border: null,
	focusRing: mainTheme.shadow.focusDanger,
};

export const ButtonColors: { [key in ButtonVariant]: ButtonColorDefinition } = {
	strong: strongVariant,
	outline: outlineVariant,
	dimmed: dimmedVariant,
	ghost: ghostVariant,
	link: linkVariant,
	danger: dangerVariant,
	default: outlineVariant,
	white: outlineVariant,
	primary: strongVariant,
	secondary: dimmedVariant,
	gradient: strongVariant,
};
